'use client';

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormTextarea } from '@/components/forms/form-textarea';
import { ApiError } from '@/lib/api/client';
import { createStorageLocationSchema } from '../schemas';
import { useUpdateStorageLocation } from '../services/storage-locations.mutations';
import type { StorageLocation } from '../types';

interface StorageLocationEditDialogProps {
  storageLocation: StorageLocation | null;
  onOpenChange: (open: boolean) => void;
}

export function StorageLocationEditDialog({
  storageLocation,
  onOpenChange
}: StorageLocationEditDialogProps) {
  const t = useTranslations('storageLocations');
  const tv = useTranslations('storageLocations.validation');

  const schema = useMemo(
    () => createStorageLocationSchema((key) => tv(key)),
    [tv]
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: '', capacity: '', notes: '' }
  });

  useEffect(() => {
    if (storageLocation) {
      form.reset({
        name: storageLocation.name,
        capacity: String(storageLocation.capacity),
        notes: storageLocation.notes ?? ''
      });
    }
  }, [storageLocation, form]);

  const updateMutation = useUpdateStorageLocation();

  const onSubmit = async (data: any) => {
    if (!storageLocation) return;
    try {
      await updateMutation.mutateAsync({
        id: storageLocation.id,
        data: {
          name: data.name,
          capacity: Number(data.capacity),
          notes: data.notes || null
        }
      });
      onOpenChange(false);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 422 &&
        (error.data as any)?.errors?.name
      ) {
        form.setError('name', {
          message: (error.data as any).errors.name[0]
        });
      }
    }
  };

  return (
    <Dialog open={!!storageLocation} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>{t('editTitle')}</DialogTitle>
        </DialogHeader>

        <Form
          form={form}
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-4'
        >
          <FormInput
            control={form.control}
            name='name'
            label={t('form.locationName')}
            placeholder={t('form.locationNamePlaceholder')}
            required
          />

          <FormField
            control={form.control}
            name='capacity'
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t('form.capacity')}
                  <span className='ms-1 text-red-500'>*</span>
                </FormLabel>
                <FormControl>
                  <div className='relative'>
                    <Input
                      type='number'
                      placeholder='0'
                      className='pe-16'
                      min={1}
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        field.onChange(v === '' ? '' : parseFloat(v));
                      }}
                    />
                    <span className='text-muted-foreground absolute end-3 top-1/2 -translate-y-1/2 text-xs font-semibold'>
                      {t('form.capacityUnit')}
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormTextarea
            control={form.control}
            name='notes'
            label={t('form.notes')}
            placeholder={t('form.notesPlaceholder')}
          />

          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='ghost'
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              {t('form.discard')}
            </Button>
            <Button type='submit' disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('form.saving') : t('form.save')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
